// src/services/api_url.ts
import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';

export interface AuthResponse {
    success: boolean;
    telegram_id: string;
    message: string;
}

export interface UserResponse {
    telegram_id: string;
    username?: string;
    email?: string;
    cash: number;
    url_key?: string;
    photo_url?: string;
    first_name?: string;
    last_name?: string;
}

export interface PaymentData {
    user_id: string;
    device_count: number;
    months: number;
    payment_method: string;
    promo_code?: string;
}

export interface PaymentResponse {
    success: boolean;
    payment_id: string;
    payment_url: string;
    amount: number;
    description: string;
}

export interface PaymentStatus {
    id: string;
    user_id: string;
    amount: number;
    label_sub: string;
    payment_time: string;
    status: 'pending' | 'success' | 'failed';
}

export interface Device {
    id: number;
    user_id: string;
    name: string | null;
    uuid_device: string | null;
    protocol_id: number | null;
    server_id: number | null;
    kind: boolean | null;
    path_or_url: string | null;
    expires_at: string;
    is_configured: boolean;
    server_name: string | null;  // Название сервера из таблицы Server
    server_ip: string | null;     // IP сервера (оставляем для совместимости)
    protocol_name: string | null;
}

export interface DevicesResponse {
    success: boolean;
    user_id: string;
    devices: Device[];
    total: number;
    device_limit: number;
    configured_count: number;
    pending_count: number;
}

export interface ApplyPromoRequest {
    user_id: string;
    promo_code: string;
    amount: number;
}

export interface ApplyPromoResponse {
    success: boolean;
    message: string;
    new_amount?: number;
    discount?: number;
    promo_type?: string;
}

export interface ApplyPromoToDeviceRequest {
    user_id: string;
    promo_code: string;
    device_id: number;
    days: number;
}

export interface ApplyPromoToDeviceResponse {
    success: boolean;
    message: string;
    new_expires_at?: string;
}

export interface ExtendDevicesRequest {
    user_id: string;
    device_ids: number[];
    months: number;
    payment_method: string;
    promo_code?: string;
    extra_devices?: number;
}

export interface Server {
    id: number;
    ip: string;
    name: string;
    protocol_id: number;
    free_slots: number;
    max_slots: number;
    protocol_name: string;
    used_slots: number;
}

export interface Protocol {
    id: number;
    name: string;
}

export interface DeviceConfigResponse {
    success: boolean;
    config: string;
    type: string;
    filename: string;
}

export interface CryptoPaymentData {
    user_id: string;
    device_count: number;
    months: number;
    amount: number;
    network: string;
    currency: string;
}

export interface CryptoPaymentResponse {
    success: boolean;
    payment_id: string;
    heleket_response: {
        state: number;
        result: {
            uuid: string;
            order_id: string;
            url: string;  // Это главное - ссылка на платежную страницу
            amount: string;
            currency: string;
            payment_status: string;
            expired_at: number;
            // ... другие поля
        };
    };
}

export interface CryptoNetwork {
    code: string;
    name: string;
    currency: string;
    network: string;
}

export interface ReferralInfoResponse {
    success: boolean;
    referral_info: {
        referral_code: string;
        referral_count: number;
        referral_earnings: number;
    };
    stats: {
        total_referrals: number;
        total_earnings: number;
        total_purchases: number;
        available_bonus_days: number;
        referrals: Array<{
            user_id: string;
            username: string | null;
            created_at: string;
            purchase_amount: number;
            earned: number;
        }>;
    };
    bonus_days: number;
}

export interface ApplyBonusResponse {
    success: boolean;
    message: string;
    applied_days?: number;
    new_expires_at?: string;
}

export interface BonusItem {
    id: string;
    referred_id: string;
    referred_username: string | null;
    available_days: number;
    created_at: string;
}

export interface AvailableBonusResponse {
    success: boolean;
    total_bonus_days: number;
    bonuses: BonusItem[];
}

export interface ApplyPartialBonusRequest {
    user_id: string;
    device_id: number;
    days_to_apply: number;
    bonus_ids: string[];
}

export interface ApplyPartialBonusResponse {
    success: boolean;
    message: string;
    applied_days?: number;
    new_expires_at?: string;
}

export type CheckAuthResult =
    | { status: 'authenticated'; user: { telegram_id: string } }
    | { status: 'unauthorized' }
    | { status: 'error'; message: string };

type RetryableRequestConfig = AxiosRequestConfig & {
    retryable?: boolean;
    maxRetries?: number;
    __retryCount?: number;
};

type CheckAuthOptions = {
    force?: boolean;
};

class ApiService {
    private api: AxiosInstance;
    private baseURL: string;
    private pendingGetRequests = new Map<string, Promise<any>>();
    private authCache: { value: CheckAuthResult; expiresAt: number } | null = null;

    constructor() {
        this.baseURL = typeof process !== 'undefined' && process.env && process.env.REACT_APP_API_URL
            ? process.env.REACT_APP_API_URL
            : 'https://api.shadownet.nl';

        this.api = axios.create({
            baseURL: this.baseURL,
            withCredentials: true,
            headers: {
                'Content-Type': 'application/json',
            },
            timeout: 10000,
        });

        this.api.interceptors.request.use((config) => {
            const telegramWebApp = (window as any).Telegram?.WebApp;
            if (telegramWebApp?.initDataUnsafe?.user) {
                config.headers['X-Telegram-Id'] = telegramWebApp.initDataUnsafe.user.id.toString();
            }
            return config;
        });

        this.api.interceptors.response.use(
            response => response,
            async (error) => {
                const config = error.config as RetryableRequestConfig | undefined;

                if (!config) {
                    return Promise.reject(error);
                }

                const method = (config.method || 'get').toLowerCase();

                const isSafeMethod = method === 'get';
                const isExplicitlyRetryable = config.retryable === true;

                if (!isSafeMethod && !isExplicitlyRetryable) {
                    return Promise.reject(error);
                }

                if (!this.shouldRetry(error)) {
                    return Promise.reject(error);
                }

                config.__retryCount = config.__retryCount || 0;
                const maxRetries = config.maxRetries ?? 2;

                if (config.__retryCount >= maxRetries) {
                    return Promise.reject(error);
                }

                config.__retryCount += 1;

                const delay = 500 * config.__retryCount;

                await this.sleep(delay);

                return this.api(config);
            }
        );
    }

    private sleep(ms: number): Promise<void> {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    private shouldRetry(error: any): boolean {
        if (!axios.isAxiosError(error)) return false;

        const status = error.response?.status;
        const isNetworkError = !error.response;
        const isTimeout = error.code === 'ECONNABORTED';
        const isServerError = status === 502 || status === 503 || status === 504;
        const isTooManyRequests = status === 429;

        return isNetworkError || isTimeout || isServerError || isTooManyRequests;
    }


    private clearAuthCache(): void {
        this.authCache = null;
    }

    private getAuthCache(force: boolean = false): CheckAuthResult | null {
        if (force || !this.authCache) {
            return null;
        }

        if (Date.now() > this.authCache.expiresAt) {
            this.authCache = null;
            return null;
        }

        return this.authCache.value;
    }

    private setAuthCache(value: CheckAuthResult, ttlMs: number = 3000): void {
        this.authCache = {
            value,
            expiresAt: Date.now() + ttlMs,
        };
    }

    private async dedupeGetRequest<T>(key: string, factory: () => Promise<T>): Promise<T> {
        const pending = this.pendingGetRequests.get(key);
        if (pending) {
            return pending as Promise<T>;
        }

        const request = factory().finally(() => {
            this.pendingGetRequests.delete(key);
        });

        this.pendingGetRequests.set(key, request);
        return request;
    }

    /**
     * Установка куки на фронтенде
     */
    private setCookie(name: string, value: string, days: number = 30): void {
        const expires = new Date();
        expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
        document.cookie = `${name}=${value}; expires=${expires.toUTCString()}; path=/; domain=.appnet.shadownet.nl; SameSite=Lax; Secure`;
    }
    /**
     * Получение tg_id из куки
     */
    getTgIdFromCookie(): string | null {
        const cookies = document.cookie.split('; ');
        for (const cookie of cookies) {
            const [name, value] = cookie.split('=');
            if (name === 'tg_id') {
                return value;
            }
        }
        return null;
    }

    /**
     * Удаление куки
     */
    removeCookie(name: string): void {
        document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/; domain=.appnet.shadownet.nl;`;
    }

    /**
     * Аутентификация по уникальному ключу
     */
    async authenticateWithKey(url_key: string): Promise<AuthResponse> {
        try {
            const response = await this.api.get<AuthResponse>(`/key/${url_key}`);

            this.clearAuthCache();

            // Убираем setCookie - сервер сам установит httpOnly куку
            // this.setCookie('tg_id', response.data.telegram_id);

            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * НОВЫЙ МЕТОД: Проверка авторизации через сервер
     */
    async checkAuth(options: CheckAuthOptions = {}): Promise<CheckAuthResult> {
        const cached = this.getAuthCache(options.force);
        if (cached) {
            return cached;
        }

        try {
            const result = await this.dedupeGetRequest<CheckAuthResult>('auth/check', async () => {
                const response = await this.api.get('/auth/check');
                const data = response.data;

                if (data?.isAuthenticated && data?.user?.telegram_id) {
                    return {
                        status: 'authenticated',
                        user: data.user,
                    };
                }

                return { status: 'unauthorized' };
            });

            this.setAuthCache(result, result.status === 'authenticated' ? 5000 : 1500);
            return result;
        } catch (error) {
            if (axios.isAxiosError(error)) {
                const status = error.response?.status;

                if (status === 401 || status === 403) {
                    const result: CheckAuthResult = { status: 'unauthorized' };
                    this.setAuthCache(result, 1500);
                    return result;
                }

                return {
                    status: 'error',
                    message: error.message || 'Ошибка сети или сервера',
                };
            }

            return {
                status: 'error',
                message: 'Неизвестная ошибка',
            };
        }
    }

    /**
     * Получение данных пользователя по telegram_id
     * @param telegram_id - ID пользователя в Telegram
     */
    async getUserData(telegram_id: string): Promise<UserResponse | null> {
        try {
            return await this.dedupeGetRequest<UserResponse | null>(`user/${telegram_id}`, async () => {
                const response = await this.api.get<UserResponse>(`/user/${telegram_id}`);
                return response.data;
            });
        } catch (error) {
            return null;
        }
    }

    /**
     * Проверка аутентификации по наличию куки
     */
    /*async checkAuth(): Promise<boolean> {
        return !!this.getTgIdFromCookie();
    }*/

    /**
     * Получение Telegram ID из куки
     */
    async getTgId(): Promise<string | null> {
        return this.getTgIdFromCookie();
    }

    /**
     * Выход из системы (удаление куки)
     */
    async logout(): Promise<void> {
        this.removeCookie('tg_id');
        this.clearAuthCache();
    }

    /**
     * Получение базового URL API
     */
    getBaseURL(): string {
        return this.baseURL;
    }

    /**
     * Создание платежа
     */
    async createPayment(paymentData: PaymentData): Promise<PaymentResponse> {
        try {
            const response = await this.api.post<PaymentResponse>('/create-payment', paymentData);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Проверка статуса платежа
     */
    async getPaymentStatus(payment_id: string): Promise<PaymentStatus> {
        try {
            const response = await this.api.get<PaymentStatus>(`/payment-status/${payment_id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Получение устройств пользователя
     */
    async getUserDevices(user_id: string): Promise<DevicesResponse> {
        try {
            return await this.dedupeGetRequest<DevicesResponse>(`user-devices/${user_id}`, async () => {
                const response = await this.api.get<DevicesResponse>(`/user-devices/${user_id}`);
                return response.data;
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Применение промокода
     */
    async applyPromoPayment(data: ApplyPromoRequest): Promise<ApplyPromoResponse> {
        try {
            const response = await this.api.post<ApplyPromoResponse>('/apply-promo-payment', data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Применение промокода
     */
    async applyPromo(data: ApplyPromoRequest): Promise<ApplyPromoResponse> {
        try {
            const response = await this.api.post<ApplyPromoResponse>('/apply-promo', data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Применение промокода на дни к устройству
     */
    async applyPromoToDevice(data: ApplyPromoToDeviceRequest): Promise<ApplyPromoToDeviceResponse> {
        try {
            const response = await this.api.post<ApplyPromoToDeviceResponse>('/apply-promo-to-device', data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Создание платежа для продления устройств
     */
    async createPaymentExtend(data: ExtendDevicesRequest): Promise<PaymentResponse> {
        try {
            const response = await this.api.post<PaymentResponse>('/create-payment-extend', data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Получение списка протоколов
     */
    async getProtocols(): Promise<Protocol[]> {
        try {
            return await this.dedupeGetRequest<Protocol[]>('protocols', async () => {
                const response = await this.api.get<Protocol[]>('/protocols');
                return response.data;
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Получение серверов по протоколу
     */
    async getServersByProtocol(protocolId: number): Promise<Server[]> {
        try {
            return await this.dedupeGetRequest<Server[]>(`servers/${protocolId}`, async () => {
                const response = await this.api.get<Server[]>(`/servers/${protocolId}`);
                return response.data;
            });
        } catch (error) {
            throw error;
        }
    }

    /**
     * Создание VLESS устройства
     */
    async createVlessDevice(deviceId: number, userId: string): Promise<any> {
        try {
            const response = await this.api.post(`/devices/${deviceId}/vless`, {
                user_id: userId,
                device_id: deviceId
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Создание WG/AWG устройства
     */
    async createWgDevice(deviceId: number, serverId: number, userId: string): Promise<any> {
        try {
            const response = await this.api.post(`/devices/${deviceId}/wg`, {
                user_id: userId,
                server_id: serverId
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Получение конфига устройства
     */
    async getDeviceConfig(deviceId: number, userId: string): Promise<DeviceConfigResponse> {
        try {
            const response = await this.api.get<DeviceConfigResponse>(`/devices/${deviceId}/config?user_id=${userId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Удаление устройства
     */
    async deleteDevice(deviceId: number, userId: string): Promise<any> {
        try {
            const response = await this.api.delete(`/devices/${deviceId}?user_id=${userId}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Создание крипто-платежа
     */
    async createCryptoPayment(data: CryptoPaymentData): Promise<CryptoPaymentResponse> {
        try {
            const response = await this.api.post<CryptoPaymentResponse>('/create-crypto-payment', data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Получение списка поддерживаемых сетей
     */
    async getCryptoNetworks(): Promise<CryptoNetwork[]> {
        try {
            const response = await this.api.get<{ networks: CryptoNetwork[] }>('/crypto-networks');
            return response.data.networks;
        } catch (error) {
            return [
                { code: 'TRX', name: 'TRC20 (USDT)', currency: 'USDT', network: 'TRC20' },
                { code: 'ETH', name: 'ERC20 (USDT)', currency: 'USDT', network: 'ERC20' },
                { code: 'BSC', name: 'BEP20 (USDT)', currency: 'USDT', network: 'BEP20' }
            ];
        }
    }

    async getReferralInfo(user_id: string): Promise<ReferralInfoResponse> {
        try {
            const response = await this.api.get<ReferralInfoResponse>(`/referral/info/${user_id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    async applyBonusDays(params: {
        user_id: string;
        device_id: number;
        days_to_apply?: number;
    }): Promise<ApplyBonusResponse> {
        try {
            const response = await this.api.post<ApplyBonusResponse>('/referral/apply-bonus', params);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Получение истории платежей пользователя (только успешные)
     */
    async getUserPayments(user_id: string): Promise<{
        success: boolean;
        user_id: string;
        payments: Array<{
            id: string;
            user_id: string;
            amount: number;
            label_sub: string;
            payment_time: string;
            status: string;
            operation_type: 'subscription' | 'extend';
            months: number;
            devices_count: number;
            description: string;
        }>;
        total: number;
    }> {
        try {
            const response = await this.api.get(`/user-payments/${user_id}`);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Создание устройства для промокода (когда нет устройств)
     */
    async createDeviceForPromo(data: { user_id: string; days: number; promo_code: string }): Promise<{
        success: boolean;
        device_id?: number;
        device_name?: string;
        expires_at?: string;
        message?: string;
    }> {
        try {
            const response = await this.api.post('/create-device-for-promo', data);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Обновление названия устройства
     */
    async updateDeviceName(deviceId: number, userId: string, name: string): Promise<{
        success: boolean;
        message: string;
        new_name?: string;
    }> {
        try {
            const response = await this.api.post(`/devices/${deviceId}/update-name`, {
                user_id: userId,
                device_id: deviceId,
                name: name
            });
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Получение статистики для админов
     */
    async getAdminStats(config?: any): Promise<{
        success: boolean;
        stats: {
            total_payments: { count: number; amount: number };
            active_subscriptions: number;
            total_users: number;
            churned_last_30d: number;
            monthly_payments: Array<{ month: string; count: number; amount: number }>;
            new_users_growth: Array<{ month: string; new_users: number }>;
        };
    }> {
        try {
            const response = await this.api.get('/admin/stats', config);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Получение реферальной статистики для админов
     */
    async getAdminReferralStats(config?: any): Promise<{
        success: boolean;
        referral_stats: Array<{
            referrer_id: string;
            username: string | null;
            total_referrals: number;
            paying_referrals: number;
            total_amount: number;
            total_earned: number;
            conversion_rate: number;
            referrals: Array<{
                user_id: string;
                username: string | null;
                created_at: string | null;
                purchase_amount: number;
                earned: number;
                has_payment: boolean;
            }>;
        }>;
        total_referrers: number;
    }> {
        try {
            const response = await this.api.get('/admin/referral-stats', config);
            return response.data;
        } catch (error) {
            throw error;
        }
    }

    /**
     * Получение списка ID администраторов из переменной окружения
     */
    getAdminIds(): string[] {
        const adminIdsStr = process.env.REACT_APP_ADMIN_IDS || process.env.NEXT_PUBLIC_ADMIN_IDS || '';
        return adminIdsStr.split(',').map(id => id.trim()).filter(id => id);
    }

    /**
     * Проверка, является ли пользователь администратором
     */
    async isUserAdmin(telegramId: string): Promise<boolean> {
        const adminIds = this.getAdminIds();
        return adminIds.includes(telegramId);
    }

    /**
     * Получение всех успешных платежей пользователей (только для админов)
     */
    async getAdminAllPayments(limit: number = 100, offset: number = 0, config?: any): Promise<{
        success: boolean;
        payments: Array<{
            id: string;
            user_id: string;
            amount: number;
            label_sub: string;
            payment_time: string;
            status: string;
            username: string | null;
            email: string | null;
        }>;
        total: number;
        monthly_stats: Array<{
            month: string;
            count: number;
            total: number;
            unique_users: number;
        }>;
    }> {
        try {
            const response = await this.api.get(`/admin/all-payments?limit=${limit}&offset=${offset}`, config);
            return response.data;
        } catch (error) {
            throw error;
        }
    }
}

export const apiService = new ApiService();
export default apiService;